// Pure (rune-free, storage-free) helpers behind the sidebar's "new scheduled
// run" green unread dot. Kept separate from scheduled-store.svelte.ts so the
// decision logic is unit-testable under bun test — which loads plain .ts modules
// directly (no Svelte compiler), so anything importing $state runes can't run.
//
// A timer-fired scheduled run creates a brand-new chat session server-side with
// no browser involved. We map that session back to when its run fired, and flag
// it "unread" until the user opens it.

export type RunSessionMap = Record<string, number>; // sessionId → run epoch (ms)
export type OpenedMap = Record<string, number>; // sessionId → last-opened epoch (ms)

export type ScheduleRunInfo = {
	lastRunAt: string | null;
	lastRunSessionId?: string | null;
	lastRunStatus?: 'running' | 'ok' | 'failed' | 'skipped' | null;
};

/** Fold the latest FINISHED run of each schedule into the session→runAt map.
 *  A still-running run isn't "new content to read" yet (and the sidebar already
 *  shows running separately), and a 'skipped' run is a conditional run the agent
 *  chose to keep silent (its chat is trashed) — neither should light the dot. Merges
 *  into `prev`; a schedule's newer run overwrites its session entry. */
export function foldRunSessions(prev: RunSessionMap, chats: ScheduleRunInfo[]): RunSessionMap {
	const next = { ...prev };
	for (const c of chats) {
		if (c.lastRunStatus === 'running' || c.lastRunStatus === 'skipped') continue;
		if (c.lastRunSessionId && c.lastRunAt) {
			const at = Date.parse(c.lastRunAt);
			if (!Number.isNaN(at)) next[c.lastRunSessionId] = at;
		}
	}
	return next;
}

/** A session is unread when it's a known scheduled run AND the user hasn't
 *  opened it since that run fired. */
export function isUnread(runSessions: RunSessionMap, openedAt: OpenedMap, sessionId: string): boolean {
	const runAt = runSessions[sessionId];
	if (!runAt) return false;
	return runAt > (openedAt[sessionId] ?? 0);
}

/** Record an open (monotonic — never moves a timestamp backwards). Returns the
 *  SAME reference when nothing changed, so the caller can skip a $state write. */
export function recordOpened(openedAt: OpenedMap, sessionId: string, at: number): OpenedMap {
	if (!sessionId || (openedAt[sessionId] ?? 0) >= at) return openedAt;
	return { ...openedAt, [sessionId]: at };
}

/** Bound storage growth: keep the newest `cap` opens by timestamp. */
export function capOpened(openedAt: OpenedMap, cap: number): OpenedMap {
	const entries = Object.entries(openedAt);
	if (entries.length <= cap) return openedAt;
	return Object.fromEntries(entries.sort((a, b) => b[1] - a[1]).slice(0, cap));
}
